import { lazy } from "react";
import { Route } from "react-router-dom";
import ModuleLockGuard from "@/components/common/ModuleLockGuard";
import { FEATURE_KEYS } from "@/lib/feature-keys";

const AcademyHome = lazy(() => import("@/pages/student/AcademyHome"));
const CourseCatalog = lazy(() => import("@/pages/student/CourseCatalog"));
const CourseDetail = lazy(() => import("@/pages/student/CourseDetail"));
const ProductsCatalog = lazy(() => import("@/pages/student/ProductsCatalog"));
const ProductDetail = lazy(() => import("@/pages/student/ProductDetail"));
const SessionsCatalog = lazy(() => import("@/pages/student/SessionsCatalog"));
const SessionDetail = lazy(() => import("@/pages/student/SessionDetail"));
const SessionTypeDetail = lazy(() => import("@/pages/student/SessionTypeDetail"));
const MembershipLanding = lazy(() => import("@/pages/student/MembershipLanding"));
const AcademyCustomPage = lazy(() => import("@/pages/student/AcademyCustomPage"));
const AcademyNotFound = lazy(() => import("@/pages/student/AcademyNotFound"));

/**
 * Single source of truth for academy PUBLIC routes (catalogs + landings).
 *
 * Rendered identically on:
 *   - Platform host inside `/:slug` → `<AcademySlugShell />`
 *   - Custom domain inside `<CustomDomainShell />`
 *
 * Paths are RELATIVE. The parent layout adds the slug prefix on the
 * platform host or roots them at "/" on the custom domain.
 *
 * Note: `index` (AcademyHome) and the wildcard "*" fallback are NOT
 * included here — callers add them themselves because their position in
 * the Routes tree differs slightly between the two surfaces (index on
 * platform host, index + "*" → AcademyHome on custom domain).
 */
export default function academyPublicRoutes() {
  return [
    <Route key="ap-courses" path="courses" element={<CourseCatalog />} />,
    <Route key="ap-course" path="courses/:courseId" element={<CourseDetail />} />,
    <Route key="ap-products" path="products" element={<ProductsCatalog />} />,
    <Route key="ap-product" path="products/:productId" element={<ProductDetail />} />,
    // Public sessions surfaces (catalog + landing) are NEVER plan-gated at
    // the route level. The Pro `LIVE_SESSIONS` gate is enforced INSIDE the
    // pages — they show a graceful empty/landing state on Basic instead of
    // a redirect-to-dashboard. The nav link itself is hidden on Basic by
    // `AcademyPublicLayout`, so the only way to reach these on Basic is a
    // direct URL, which must still render a clean academy-scoped landing.
    <Route key="ap-sessions" path="sessions" element={<SessionsCatalog />} />,
    <Route key="ap-session" path="sessions/:sessionId" element={<SessionDetail />} />,
    // Public session-type landing (Phase 4). Like sessions, this is NEVER
    // route-gated by plan — a direct URL must always render a clean
    // academy-scoped landing. Cross-academy IDs return 404 inside the page.
    <Route key="ap-session-type" path="session-types/:sessionTypeId" element={<SessionTypeDetail />} />,
    <Route
      key="ap-membership"
      path="membership"
      element={
        <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.VIP_MEMBERSHIP}>
          <MembershipLanding />
        </ModuleLockGuard>
      }
    />,
    <Route key="ap-page" path="page/:pageSlug" element={<AcademyCustomPage />} />,
  ];
}

/**
 * Unified academy 404 catch-all route.
 *
 * Mounted as the LAST child route inside both `<AcademySlugShell />` (platform)
 * and `<CustomDomainShell />` (custom domain). Returns the same `<AcademyNotFound />`
 * page on both surfaces so a stray URL produces an identical experience.
 */
export function academyNotFoundRoute() {
  return <Route key="ap-404" path="*" element={<AcademyNotFound />} />;
}

export { AcademyHome };